import { Link } from 'react-router';
import { SEO } from '@/components/seo/seo';

export function TermsPage() {
  return (
    <div className="relative">
      <SEO
        title="Terms of Service"
        description="The terms that apply when you use Fixtyer to prepare a UK pothole damage claim pack."
        canonical="/terms"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="flex items-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Terms of Service</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 leading-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-lg text-surface-800">
            Last updated: 13 January 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Welcome Box */}
            <div className="bg-brand-50 rounded-2xl p-8 mb-12 border border-brand-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-brand-900 mt-0 mb-2">Welcome to Fixtyer</h2>
                  <p className="text-surface-800 m-0">
                    These Terms of Service ("Terms") govern your use of Fixtyer's services. By using Fixtyer, 
                    you agree to these Terms. We've written them in plain English wherever possible, but some 
                    legal language is necessary to protect both you and us.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <TermsSection number={1} title="What Fixtyer Does">
              <p>
                Fixtyer is an online service that helps you create documents for making pothole damage claims. We provide:
              </p>
              <ul>
                <li>Templates and guidance for creating claim documents</li>
                <li>Tools to organise evidence and prepare claim documents</li>
                <li>Information about the claims process</li>
              </ul>
              <ImportantNote>
                Fixtyer is a document preparation service, not a law firm. We don't provide legal advice, 
                representation, or guarantees about claim outcomes. For complex legal matters or if you're 
                unsure about your situation, please consult a qualified solicitor.
              </ImportantNote>
            </TermsSection>

            {/* Section 2 */}
            <TermsSection number={2} title="Your Account">
              <h4 className="font-display font-semibold text-brand-900">Creating an Account</h4>
              <ul>
                <li>You must be at least 18 years old to use Fixtyer</li>
                <li>You must provide accurate information when registering</li>
                <li>You're responsible for keeping your password secure</li>
                <li>You're responsible for all activity under your account</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">Account Security</h4>
              <p>
                If you believe your account has been compromised, please contact us immediately at{' '}
                <a href="mailto:hello@fixtyer.co.uk" className="text-brand-600 hover:text-brand-700">hello@fixtyer.co.uk</a>. 
                We're not liable for losses resulting from unauthorised use of your account if you haven't taken 
                reasonable steps to secure it.
              </p>
            </TermsSection>

            {/* Section 3 */}
            <TermsSection number={3} title="Using Our Service">
              <h4 className="font-display font-semibold text-brand-900">What You Can Do</h4>
              <ul>
                <li>Create and download documents for your personal pothole damage claims</li>
                <li>Store evidence and information related to your claims</li>
                <li>Access guidance and resources we provide</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">What You Can't Do</h4>
              <ul>
                <li>Share your account with others</li>
                <li>Use the service for any illegal purpose</li>
                <li>Upload malicious code or attempt to breach our security</li>
                <li>Copy, modify, or distribute our content without permission</li>
                <li>Use the service to create documents for others commercially</li>
                <li>Present our documents as legal advice or legal representation</li>
              </ul>
            </TermsSection>

            {/* Section 4 */}
            <TermsSection number={4} title="Your Content and Data">
              <h4 className="font-display font-semibold text-brand-900">What You Own</h4>
              <p>
                You retain all rights to the information, photos, and content you upload to Fixtyer. 
                By using our service, you grant us a licence to process and store this data to provide our service to you.
              </p>

              <h4 className="font-display font-semibold text-brand-900">What We'll Do With Your Data</h4>
              <p>We'll only use your data to:</p>
              <ul>
                <li>Provide and improve our service</li>
                <li>Communicate with you about your account</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p>
                We won't sell your data to third parties. For full details, see our{' '}
                <Link to="/privacy" className="text-brand-600 hover:text-brand-700">Privacy Policy</Link>.
              </p>

              <h4 className="font-display font-semibold text-brand-900">Your Responsibilities</h4>
              <p>You're responsible for ensuring:</p>
              <ul>
                <li>The accuracy of information you provide</li>
                <li>You have the right to upload any content you share</li>
                <li>Your content doesn't infringe anyone else's rights</li>
                <li>You keep backups of important information</li>
              </ul>
            </TermsSection>

            {/* Section 5 */}
            <TermsSection number={5} title="Documents We Generate">
              <h4 className="font-display font-semibold text-brand-900">Quality and Accuracy</h4>
              <p>Whilst we work hard to ensure our templates and guidance are accurate and up-to-date, we can't guarantee that:</p>
              <ul>
                <li>Documents we help you create will be accepted by all authorities</li>
                <li>The information provided is suitable for your specific circumstances</li>
                <li>Claims made using our documents will be successful</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">Your Review Responsibility</h4>
              <ImportantNote>
                You must review all documents before using them. You're responsible for ensuring the information 
                is accurate and complete. If you're unsure about anything, please seek professional legal advice.
              </ImportantNote>

              <h4 className="font-display font-semibold text-brand-900">No Legal Advice</h4>
              <p>
                Fixtyer provides document templates and general information only. Nothing we provide constitutes 
                legal advice. Each claim is unique, and we can't advise on the merits of your specific case.
              </p>
            </TermsSection>

            {/* Section 6 */}
            <TermsSection number={6} title="Payments">
              <h4 className="font-display font-semibold text-brand-900">Pricing</h4>
              <p>Our current prices are:</p>
              <ul>
                <li><strong>Council Claim Pack:</strong> £35</li>
              </ul>
              <p>Small claims court document preparation is planned but is not currently available to purchase.</p>
              <p>
                Prices are displayed on our website and may change from time to time. Any price changes will not affect purchases you have already made.
              </p>

              <h4 className="font-display font-semibold text-brand-900">Payment Terms</h4>
              <p>Each paid pack is a one-off purchase, with no subscription. When you pay:</p>
              <ul>
                <li>You receive immediate access to the relevant service</li>
                <li>Your payment covers the preparation of one claim pack</li>
                <li>There are no recurring charges or automatic renewals</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">What's Included</h4>
              <p>
                Your payment covers our assistance in preparing your claim documents, including template review, 
                document generation, and guidance through the process. It does not include:
              </p>
              <ul>
                <li>Legal advice</li>
                <li>Submission of claims on your behalf</li>
                <li>Guaranteed outcomes</li>
                <li>Ongoing support beyond your initial claim (unless you decide to pursue our court claims pack guide)</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">Refunds</h4>
              <p>
                Personalised preparation begins after successful payment. Once that work has
                begun, cancellation and refund rights may be limited for the personalised digital
                service, subject to your statutory consumer rights. If a technical problem prevents
                you from using the service, contact us so we can fix it or provide a refund where
                appropriate.
              </p>
            </TermsSection>

            {/* Section 7 */}
            <TermsSection number={7} title="Service Availability">
              <h4 className="font-display font-semibold text-brand-900">What We Aim For</h4>
              <p>We strive to keep Fixtyer available 24/7, but we can't guarantee uninterrupted access. We may need to suspend the service for:</p>
              <ul>
                <li>Scheduled maintenance (we'll try to give notice)</li>
                <li>Emergency repairs</li>
                <li>Security updates</li>
                <li>Legal or regulatory reasons</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">No Guarantee of Outcomes</h4>
              <p>We provide tools to help you create documents, but we don't guarantee:</p>
              <ul>
                <li>That councils will accept your claim</li>
                <li>Any particular outcome from using our service</li>
                <li>That the service will meet all your specific needs</li>
              </ul>
            </TermsSection>

            {/* Section 8 */}
            <TermsSection number={8} title="Our Intellectual Property">
              <p>
                The Fixtyer platform, including our software, design, templates, and content, is protected by 
                copyright and other intellectual property laws. You may use our service but you don't acquire 
                any ownership rights in our intellectual property.
              </p>
            </TermsSection>

            {/* Section 9 */}
            <TermsSection number={9} title="Liability and Disclaimers">
              <h4 className="font-display font-semibold text-brand-900">What We're Responsible For</h4>
              <p>Nothing in these Terms limits our liability for:</p>
              <ul>
                <li>Death or personal injury caused by our negligence</li>
                <li>Fraud or fraudulent misrepresentation</li>
                <li>Anything else that can't be limited by UK law</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">What We're Not Responsible For</h4>
              <p>To the fullest extent permitted by law, we're not liable for:</p>
              <ul>
                <li>The outcome of any claims you make</li>
                <li>Decisions made by councils or other authorities</li>
                <li>Losses resulting from incorrect or incomplete information you provide</li>
                <li>Your reliance on information provided without seeking professional advice</li>
                <li>Indirect or consequential losses</li>
                <li>Loss of opportunity, profits, or business</li>
                <li>Any losses beyond the amount you've paid us in the 12 months before the issue arose</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">Fair and Reasonable</h4>
              <p>These limitations are reasonable given:</p>
              <ul>
                <li>We charge modest fees for our service</li>
                <li>We provide templates and tools, not professional legal services</li>
                <li>Many factors affecting claim success are beyond our control</li>
                <li>You remain responsible for reviewing and using documents appropriately</li>
              </ul>
            </TermsSection>

            {/* Section 10 */}
            <TermsSection number={10} title="Your Promises to Us (Indemnity)">
              <p>You agree to defend and indemnify us against claims arising from:</p>
              <ul>
                <li>Your misuse of our service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any law or third-party rights</li>
                <li>Content you upload or information you provide</li>
              </ul>
              <p>
                This doesn't apply if the claim is due to our negligence or fault.
              </p>
            </TermsSection>

            {/* Section 11 */}
            <TermsSection number={11} title="Termination">
              <h4 className="font-display font-semibold text-brand-900">You Can Leave Any Time</h4>
              <p>
                You can ask us to close your account at any time through our contact route.
                Closing an account does not create or end a recurring paid period because packs
                are one-off purchases.
              </p>

              <h4 className="font-display font-semibold text-brand-900">We Might Need to Suspend or Terminate</h4>
              <p>We may suspend or terminate your account if:</p>
              <ul>
                <li>You breach these Terms</li>
                <li>You use the service fraudulently or illegally</li>
                <li>We're required to by law</li>
                <li>We decide to discontinue the service (with reasonable notice)</li>
              </ul>
              <p>
                If we end access because of a breach, a completed personalised pack is not
                automatically refundable. If we discontinue the service before providing a pack
                you paid for, we will provide the pack or an appropriate refund, subject to your
                statutory rights.
              </p>
            </TermsSection>

            {/* Section 12 */}
            <TermsSection number={12} title="Changes to These Terms">
              <p>
                We may update these Terms from time to time. If we make significant changes, we'll notify you by 
                email or through the service. Continued use after changes means you accept the new Terms.
              </p>
            </TermsSection>

            {/* Section 13 */}
            <TermsSection number={13} title="General Legal Bits">
              <div className="grid sm:grid-cols-2 gap-4 my-6">
                <LegalCard
                  title="Entire Agreement"
                  description="These Terms, together with our Privacy Policy, constitute the entire agreement between you and Fixtyer."
                />
                <LegalCard
                  title="Severability"
                  description="If any part of these Terms is found to be unenforceable, the rest remains in effect."
                />
                <LegalCard
                  title="No Waiver"
                  description="If we don't enforce a right under these Terms, that doesn't mean we've waived that right."
                />
                <LegalCard
                  title="Assignment"
                  description="We may transfer our rights and obligations to another organisation. You may not transfer your rights."
                />
              </div>

              <h4 className="font-display font-semibold text-brand-900">Third Parties</h4>
              <p>
                These Terms are between you and us. No one else has rights to enforce them under the 
                Contracts (Rights of Third Parties) Act 1999.
              </p>

              <h4 className="font-display font-semibold text-brand-900">Governing Law</h4>
              <p>
                These Terms are governed by the laws of England and Wales. Any disputes will be subject to 
                the exclusive jurisdiction of the courts of England and Wales.
              </p>
              <p className="text-surface-800 italic">
                However, if you're a consumer in Scotland or Northern Ireland, you can bring proceedings in 
                your local courts if you prefer.
              </p>
            </TermsSection>

            {/* Section 14 */}
            <TermsSection number={14} title="How to Contact Us">
              <p>If you have questions about these Terms or our service:</p>
              <div className="bg-surface-50 rounded-xl p-6 my-6 border border-surface-200">
                <p className="font-display font-semibold text-brand-900 mb-2">Fixtyer</p>
                <p className="text-surface-800 mb-1">Elevyn Technology Group Limited</p>
                <p className="text-surface-800 mb-1">
                  Email: <a href="mailto:hello@fixtyer.co.uk" className="text-brand-600 hover:text-brand-700">hello@fixtyer.co.uk</a>
                </p>
                <p className="text-surface-800 mb-0">Address: Cradley Enterprise Centre, Maypole Fields, Halesowen, B63 2QB</p>
              </div>
              <p>We aim to respond to all queries within 7 business days.</p>
            </TermsSection>

            {/* Consumer Rights Notice */}
            <div className="bg-accent-50 rounded-2xl p-8 mt-12 border border-accent-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-accent-900 mt-0 mb-2">Important Consumer Rights Notice</h2>
                  <p className="text-surface-800 mb-3">
                    Nothing in these Terms affects your statutory rights as a consumer under UK law. 
                    For information about your consumer rights, visit the{' '}
                    <a href="https://www.citizensadvice.org.uk/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700">
                      Citizens Advice website
                    </a>.
                  </p>
                  <p className="text-surface-800 m-0">
                    If you're not happy with how we've handled a complaint, you may be able to refer it to 
                    the relevant Alternative Dispute Resolution (ADR) provider or the Online Dispute Resolution (ODR) platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Agreement confirmation */}
            <div className="text-center mt-12 pt-8 border-t border-surface-200">
              <p className="text-lg font-semibold text-brand-950">
                By using Fixtyer, you confirm that you've read, understood, and agree to these Terms of Service.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Back to Home CTA */}
      <section className="py-12 bg-brand-50 border-t border-brand-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-surface-800 mb-4">Have questions about our Terms of Service?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
            >
              Contact Us
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-700 font-semibold rounded-xl border-2 border-brand-200 hover:border-brand-300 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Component for terms sections
function TermsSection({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 bg-accent-500 text-white text-sm font-bold rounded-lg flex items-center justify-center">
          {number}
        </span>
        <h3 className="font-display text-2xl font-semibold text-brand-950 m-0">{title}</h3>
      </div>
      <div className="text-surface-800 [&>p]:mb-4 [&>ul]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:mb-2 [&>h4]:mt-6 [&>h4]:mb-3">
        {children}
      </div>
    </div>
  );
}

// Component for important notes
function ImportantNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-warning-500/10 border-l-4 border-warning-500 rounded-r-lg p-4 my-4">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-surface-800 m-0 font-medium">{children}</p>
      </div>
    </div>
  );
}

// Card for legal terms
function LegalCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-surface-200">
      <h5 className="font-display font-semibold text-brand-900 mb-1">{title}</h5>
      <p className="text-sm text-surface-800 m-0">{description}</p>
    </div>
  );
}
